// react
import { Link as ReactLink } from 'react-router-dom';
import styled, { css } from 'styled-components';

// components
import { Icon } from '@/components/atoms';
import ProfileSummary from '@/components/molecules/ProfileSummary/ProfileSummary';
import { Icons } from '@/components/atoms/Icon';

/**
 * NavVertical is a navigation drawer that slides in from the left side of the
 * screen.
 */
export const NavVertical = ({
  className,
  imgSrc,
  fallback,
  name,
  role,
  links,
  currentLink,
  isCollapsed,
  onAfterNavigate,
  isHideSummary = false,
}: NavVerticalProps) => {
  return (
    <>
      <StyledNavVertical className={className} $isCollapsed={isCollapsed}>
        {!isHideSummary && (
          <Header>
            <ProfileSummary
              imgSrc={imgSrc}
              fallback={fallback}
              name={name}
              role={role}
              isText={!isCollapsed}
              mode='light'
            />
          </Header>
        )}
        <Links>
          {links?.map((link) => (
            <div key={link.label}>
              {link.to && (
                <StyledLink
                  key={link.label}
                  to={link.to}
                  onClick={() => {
                    if (link.onClick) {
                      link.onClick();
                    } else {
                      onAfterNavigate?.();
                    }
                  }}
                  $isCurrent={currentLink === link.to}
                  $isIconOnly={isCollapsed}
                >
                  <Icon
                    icon={link.icon}
                    fontSize={16}
                    type={currentLink === link.to ? 'fas' : 'fal'}
                  />
                  {!isCollapsed && link.label}
                </StyledLink>
              )}
            </div>
          ))}
        </Links>
      </StyledNavVertical>
    </>
  );
};

interface StyledNavVerticalProps {
  $isCollapsed?: boolean;
}

const StyledNavVertical = styled.div<StyledNavVerticalProps>`
  position: relative;
  display: inline-flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[64]};
  height: 100%;
  box-sizing: border-box;
  ${(props) =>
    !props.$isCollapsed &&
    css`
      width: 100%;
    `}
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[16]};
`;

const Links = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[20]};
  padding: 0 ${(props) => props.theme.spacing[16]};
`;

interface StyledLinkProps {
  $isIconOnly?: boolean;
  $isCurrent: boolean;
}
const StyledLink = styled(ReactLink)<StyledLinkProps>`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[12]};
  font: ${(props) => props.theme.typography.body.md.medium};
  color: ${(props) => props.theme.color.neutral[70]};
  padding-right: ${(props) => props.theme.spacing[4]};
  min-height: 36px;
  cursor: pointer;
  box-sizing: border-box;
  text-decoration: none;
  width: min-content;
  white-space: nowrap;
  transition-duration: 0.2s;
  border-bottom: 2px solid transparent;

  &:hover {
    color: ${(props) => props.theme.color.primary[80]};
  }

  ${(props) =>
    props.$isCurrent &&
    css`
      color: ${props.theme.color.primary[80]};
      border-bottom: 2px solid ${props.theme.color.primary[80]};
      pointer-events: none;
    `}

  ${(props) =>
    props.$isIconOnly &&
    css`
      justify-content: center;
    `}
`;

export interface Link {
  label: string;
  icon: Icons;
  to: string;
  onClick?: () => void;
}

interface NavVerticalProps {
  className?: string;
  imgSrc?: string;
  fallback: string;
  name: string;
  role: string;
  currentLink: string;
  links: Link[];
  isCollapsed?: boolean;
  onAfterNavigate?: () => void;
  isHideSummary?: boolean;
}

NavVertical.displayName = 'NavVertical';

export default NavVertical;
